import React, { useCallback } from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box } from '@atlaskit/primitives';

import DataClassificationLevel from '@atlassian/data-classification-level';

import { fg } from '@confluence/feature-gating';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { SpaceClassificationMetadata } from '../hooks/useDefaultAndSpaceClassificationLevels.ts';
import type { ContentClassificationMetadata } from '../hooks/useContentClassification.ts';
import { useIsDataClassificationEnabledForOrg } from '../hooks/useIsDataClassificationEnabledForOrg.ts';
import { ErrorState } from '../constants/ErrorState.ts';
import { ClassificationTagButtonError } from '../shared/ClassificationTagButtonError.tsx';
import { usePreloadClassificationEditorQueries } from '../hooks/usePreloadClassificationEditorQueries.ts';
import { getGuidelineProps } from '../utils/getGuidelineProps.ts';

const customSitesContentClassificationTagWrapperStyles = xcss({
	marginTop: 'space.025',
});

type TagProps = {
	contentId: string;
	contentType: string | null;
	isEditMode?: boolean;
	contentClassificationMetadata?: ContentClassificationMetadata;
	spaceClassificationMetadata?: SpaceClassificationMetadata;
	renderPopupComponent?: (closePopup: () => void) => React.ReactNode;
};

const messages = defineMessages({
	primaryErrorText: {
		id: 'data-classification.content-classification-tag.error-primary-text',
		defaultMessage: 'Unable to load all data',
		description: 'Primary text for classification tag loading error',
	},
	secondaryErrorText: {
		id: 'data-classification.content-classification-tag.error-secondary-text',
		defaultMessage: 'Try reloading now.',
		description: 'Secondary text for classification tag loading error',
	},
	tooltipMessage: {
		id: 'data-classification.content-classification.tag.tooltip',
		defaultMessage: 'This {contentType} is classified as {classification}',
		description: 'Tooltip text for classification',
	},
});

const DataClassificationLevelMemo = React.memo(DataClassificationLevel);

const ContentClassificationTagComponent = ({
	contentType,
	contentClassificationMetadata,
	isEditMode = false,
	renderPopupComponent,
}: TagProps) => {
	const intl = useIntl();
	const { cloudId, orgId } = useSessionData();
	const spaceId = useSpaceId();
	const spaceKey = useSpaceKey();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { error: dataClassificationEnabledForOrgError } = useIsDataClassificationEnabledForOrg();

	const preloadClassificationEditorQueries = usePreloadClassificationEditorQueries({ spaceKey });

	const handlePopupShown = useCallback(() => {
		if (!isEditMode) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'dataClassificationEditPopup',
				action: 'viewed',
				actionSubject: 'popup',
				actionSubjectId: 'editorTitleDataClassificationPopup',
				attributes: {
					cloudId,
					spaceId,
					orgId,
				},
			},
		}).fire();
	}, [isEditMode, createAnalyticsEvent, cloudId, spaceId, orgId]);

	if (!contentClassificationMetadata) {
		return null;
	}

	const {
		classification: currentClassification,
		errorState,
		contentLevelError,
		shouldDisplayClassification,
	} = contentClassificationMetadata;

	// Two error handling cases resulting in early returns:
	// 1. If there's no classification to render, return null
	if (errorState === ErrorState.NULL) {
		return null;
		// 2. If there is an unhandled error (a non-404 error), return error message with error displays
	} else if (errorState === ErrorState.MESSAGE) {
		return (
			<Box
				xcss={customSitesContentClassificationTagWrapperStyles}
				testId="data-classification-level-error"
			>
				<ClassificationTagButtonError
					contentLevelError={contentLevelError}
					primaryText={messages.primaryErrorText}
					secondaryText={messages.secondaryErrorText}
				/>
			</Box>
		);
	}

	// Return error display for when there is an error fetching if data classification is disabled for org
	if (dataClassificationEnabledForOrgError) {
		return (
			<Box
				xcss={customSitesContentClassificationTagWrapperStyles}
				testId="data-classification-level-error"
			>
				<ErrorDisplay error={dataClassificationEnabledForOrgError} />
			</Box>
		);
	}

	// If data classification is completely turned off (or we're dealing with
	// non-published classifications) we don't want to display anything (even errors).
	if (!shouldDisplayClassification || !currentClassification) {
		return null;
	}

	// Classification is enabled, the classification status is published, and
	// we haven't encountered any errors. Display the classification to the user!
	const { guideline, guidelineRepresentation } = getGuidelineProps(
		currentClassification,
		fg('confluence_dlp_guideline_rich_text_support'),
	);

	return (
		<Box
			xcss={customSitesContentClassificationTagWrapperStyles}
			testId="data-classification-level"
			onMouseEnter={renderPopupComponent ? preloadClassificationEditorQueries : undefined}
		>
			<DataClassificationLevelMemo
				testId="data-classification-level"
				name={currentClassification.name}
				guideline={guideline}
				guidelineRepresentation={guidelineRepresentation}
				tooltip={intl.formatMessage(messages.tooltipMessage, {
					contentType,
					classification: currentClassification.name,
				})}
				color={currentClassification.color}
				isPopupDisabled={!guideline && !isEditMode}
				renderPopupComponent={
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
					!process.env.REACT_SSR ? renderPopupComponent || undefined : undefined
				}
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				onPopupShown={!process.env.REACT_SSR ? handlePopupShown : undefined}
			/>
		</Box>
	);
};

const ContentClassificationTagGetter: FC<TagProps> = (props) => {
	return <ContentClassificationTagComponent {...props} />;
};

const ContentClassificationTagWithErrorBoundary = withTransparentErrorBoundary({
	attribution: Attribution.DLP,
})(ContentClassificationTagGetter);

export const ContentClassificationTag: FC<TagProps> = (props) => (
	<ContentClassificationTagWithErrorBoundary {...props} />
);
